import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import {
  getAllAttendances,
  getMeetingsAttendance,
  getSchools
} from '../../API/apiService';
import BreadCrumb from '../../components/breadCrumb';
import DownloadAttendances from '../../components/downloadAttendances';
import DateRangeFilter from '../../components/dateRangeFilter';
import AdminProcessScopeFilter from '../../components/adminProcessScopeFilter';
import ItemsDescription from '../../components/assitenceAdmin/itemsDescription';
import { useLoader } from '../../contexts/loader/loader';
import CustomFilterSelect from '../../shared/customFilterSelect';
import {
  ADMIN_SCOPE_VALUES,
  buildScopedSchools,
  filterAttendancesByAdminScope,
  normalizeAdminScope
} from '../../utils/adminScope';
import './style.scss';
import { createAttendanceArchive } from '../../utils/attendanceArchive';
import {
  getDateFiltersFromSearchParams,
  isDateInRange
} from '../../utils/dateFilters';
import { ENTITY_TYPE_FILTER_OPTIONS, getEntityTypeFilterLabel, matchesEntityType } from '../../utils/entityType';
import {
  getMultiFilterLabel,
  matchesAnySelected,
  normalizeMultiSelection,
  parseMultiFilterValue,
  serializeMultiFilterValue
} from '../../utils/multiFilter';
import { buildOfficialAttendancePdfBlob } from '../officialAttendanceReport';

const ALL_FILTER_ID = '__all__';

function normalizeText(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function sortOptionsByName(options) {
  return [...options].sort((a, b) =>
    String(a?.name || '').localeCompare(String(b?.name || ''), 'es', {
      sensitivity: 'base'
    })
  );
}

function buildUniqueOptions(items, getId, getName) {
  const unique = new Map();

  items.forEach(item => {
    const id = String(getId(item) || '').trim();
    const name = String(getName(item) || '').trim();

    if (!id || !name) {
      return;
    }

    unique.set(id, { id, name });
  });

  return sortOptionsByName(Array.from(unique.values()));
}

function toNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function normalizeMeetingType(value) {
  return String(value || '').trim().toUpperCase();
}

function getUserDisplayName(user) {
  return String(user?.full_name ?? user?.fullName ?? user?.name ?? '').trim();
}

function getMeetingGroupId(record) {
  return String(
    record?.meeting_group_id ?? record?.meetingGroupId ?? record?.meetingGroup?.id ?? ''
  ).trim();
}

function getGroupId(record) {
  return String(record?.groupId ?? record?.group_id ?? record?.group?.id ?? '').trim();
}

function getSchoolId(record) {
  return String(
    record?.school?.id ??
      record?.schoolId ??
      record?.school_id ??
      record?.group?.school?.id ??
      record?.group?.schoolId ??
      record?.group?.school_id ??
      ''
  ).trim();
}

function getSchoolDisplayName(school) {
  return String(school?.name ?? school?.address ?? school?.organization ?? '').trim();
}

function getMeetingDate(record) {
  return String(record?.meeting_date ?? record?.meetingDate ?? '').trim();
}

function hasMeetingSessionData(record) {
  return Boolean(
    getMeetingDate(record) ||
      String(record?.goal ?? '').trim() ||
      String(record?.notes ?? '').trim()
  );
}

function getAttendanceRowMediatorId(row) {
  return String(
    row?.meetingGroup?.userId ??
      row?.meetingGroup?.user_id ??
      row?.meetingGroup?.user?.id ??
      row?.group?.userId ??
      row?.group?.user_id ??
      row?.group?.user?.id ??
      row?.school?.userId ??
      row?.school?.user_id ??
      row?.school?.user?.id ??
      ''
  ).trim();
}

function getAttendanceRowMediatorName(row) {
  return (
    getUserDisplayName(row?.meetingGroup?.user) ||
    getUserDisplayName(row?.group?.user) ||
    getUserDisplayName(row?.school?.user)
  );
}

function getMeetingLabel(meetingGroup) {
  const type = normalizeMeetingType(meetingGroup?.type);
  const order = String(meetingGroup?.order ?? '').trim();
  const advisorPhaseLabels = {
    COP_SOLICITUD: 'Solicitud',
    COP_PLANEACION: 'Planeacion',
    COP_DESARROLLO: 'Desarrollo',
    COP_CIERRE: 'Cierre'
  };
  if (advisorPhaseLabels[type]) {
    return `${advisorPhaseLabels[type]} - Encuentro ${order}`.trim();
  }
  const meetingName = type === 'EVENT' ? 'Evento' : 'Encuentro';
  return `${meetingName} ${order}`.trim();
}

function resolveAttendancesData(attendances) {
  const meetingGroups = Array.isArray(attendances?.meetingGroupsTable)
    ? attendances.meetingGroupsTable
    : Array.isArray(attendances?.meetings)
      ? attendances.meetings
      : [];
  const groups = Array.isArray(attendances?.groups) ? attendances.groups : [];
  const attendanceModels = Array.isArray(attendances?.attendancesModel)
    ? attendances.attendancesModel
    : [];

  return {
    meetingGroups,
    groups,
    attendanceModels
  };
}

function buildAttendanceFiltersSearch({
  scope,
  schoolId,
  schoolName,
  groupId,
  mediatorId,
  dateFrom,
  dateTo
}) {
  const nextSearchParams = new URLSearchParams();

  if (scope) nextSearchParams.set('scope', scope);
  if (schoolId) nextSearchParams.set('schoolId', schoolId);
  if (schoolName) nextSearchParams.set('schoolName', schoolName);
  if (groupId) nextSearchParams.set('groupId', groupId);
  if (mediatorId) nextSearchParams.set('mediatorId', mediatorId);
  if (dateFrom) nextSearchParams.set('dateFrom', dateFrom);
  if (dateTo) nextSearchParams.set('dateTo', dateTo);

  return nextSearchParams.toString();
}

function buildMeetingRows(attendances, scopedSchools, { includeEmptyMeetings = false } = {}) {
  const { meetingGroups, groups, attendanceModels } = resolveAttendancesData(attendances);
  const schoolById = new Map(
    (Array.isArray(scopedSchools) ? scopedSchools : []).map(school => [
      String(school?.id || '').trim(),
      school
    ])
  );
  const groupById = new Map(
    groups.map(group => [String(group?.id || '').trim(), group])
  );
  const meetingGroupById = new Map(
    meetingGroups.map(meetingGroup => [
      String(meetingGroup?.id || '').trim(),
      meetingGroup
    ])
  );
  const attendanceModelByMeetingGroupId = new Map();

  attendanceModels.forEach(attendance => {
    const meetingGroupId = getMeetingGroupId(attendance);
    if (!meetingGroupId || attendanceModelByMeetingGroupId.has(meetingGroupId)) {
      return;
    }

    attendanceModelByMeetingGroupId.set(meetingGroupId, attendance);
  });

  const meetingGroupIds = includeEmptyMeetings
    ? Array.from(
        new Set([
          ...meetingGroups
            .filter(hasMeetingSessionData)
            .map(meetingGroup => String(meetingGroup?.id || '').trim())
            .filter(Boolean),
          ...attendanceModelByMeetingGroupId.keys()
        ])
      )
    : Array.from(attendanceModelByMeetingGroupId.keys());

  return meetingGroupIds
    .map(meetingGroupId => {
      const attendanceModel = attendanceModelByMeetingGroupId.get(meetingGroupId);
      const meetingGroup =
        meetingGroupById.get(meetingGroupId) ?? attendanceModel?.meetingGroup ?? null;

      if (!meetingGroup) {
        return null;
      }

      const activityGroup = meetingGroup?.activityGroup ?? null;
      const groupId = activityGroup
        ? String(activityGroup?.id || '').trim()
        : getGroupId(meetingGroup);
      const group =
        activityGroup ?? groupById.get(groupId) ?? meetingGroup?.group ?? null;
      const activity = activityGroup?.activity ?? null;
      const schoolId = getSchoolId(group) || getSchoolId(meetingGroup);
      const baseSchool = activity
        ? {
            id: activity?.idActivity,
            code: activity?.name,
            name: activity?.line || activity?.name,
            user: activityGroup?.user,
            groups: [activityGroup]
          }
        : schoolById.get(schoolId) ?? group?.school ?? meetingGroup?.school ?? null;
      const mediatorUser = meetingGroup?.user ?? group?.user ?? baseSchool?.user ?? null;
      const schoolGroups = Array.isArray(baseSchool?.groups)
        ? baseSchool.groups
        : group
          ? [group]
          : [];
      const school = baseSchool
        ? {
            ...baseSchool,
            user: mediatorUser || baseSchool?.user,
            groups: schoolGroups
          }
        : group?.school
          ? {
              ...group.school,
              user: mediatorUser || group?.school?.user,
              groups: group ? [group] : []
            }
          : null;
      const mediatorId = getAttendanceRowMediatorId({ school, group, meetingGroup });
      const mediatorName = getAttendanceRowMediatorName({ school, group, meetingGroup });

      return {
        ...meetingGroup,
        school,
        group,
        schoolId: activity
          ? String(activity?.idActivity || '').trim()
          : schoolId,
        schoolCode: String(school?.code ?? meetingGroup?.school?.code ?? '').trim(),
        schoolName:
          getSchoolDisplayName(school) ||
          getSchoolDisplayName(meetingGroup?.school) ||
          getSchoolDisplayName(group?.school),
        groupId,
        groupName: String(
          activityGroup?.code ?? group?.code ?? meetingGroup?.group?.code ?? ''
        ).trim(),
        mediatorId,
        mediatorName,
        meetingName: getMeetingLabel(meetingGroup),
        meetingDate: getMeetingDate(meetingGroup)
      };
    })
    .filter(Boolean)
    .sort((left, right) => {
      const schoolCompare = left.schoolCode.localeCompare(right.schoolCode, 'es', {
        numeric: true,
        sensitivity: 'base'
      });
      if (schoolCompare !== 0) return schoolCompare;

      const groupCompare = left.groupName.localeCompare(right.groupName, 'es', {
        numeric: true,
        sensitivity: 'base'
      });
      if (groupCompare !== 0) return groupCompare;

      const leftOrder = toNumber(left?.order) ?? 0;
      const rightOrder = toNumber(right?.order) ?? 0;
      if (leftOrder !== rightOrder) return leftOrder - rightOrder;

      const dateCompare = String(left.meetingDate || '').localeCompare(
        String(right.meetingDate || ''),
        'es',
        { sensitivity: 'base' }
      );
      if (dateCompare !== 0) return dateCompare;

      return (toNumber(left?.id) ?? 0) - (toNumber(right?.id) ?? 0);
    });
}

function Assistence() {
  const [disabledButton, setDisabledButton] = useState(false);
  const [attendances, setAttendances] = useState(null);
  const [schools, setSchools] = useState([]);
  const [hasLoadedAttendances, setHasLoadedAttendances] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const adminScope = normalizeAdminScope(searchParams.get('scope'));
  const isCopScope = adminScope === ADMIN_SCOPE_VALUES.COP;
  const isSocialControlScope = adminScope === ADMIN_SCOPE_VALUES.CS;
  const [isDownloadingFormats, setIsDownloadingFormats] = useState(false);
  const [selectedSchoolIds, setSelectedSchoolIds] = useState(() =>
    parseMultiFilterValue(searchParams.get('schoolId'))
  );
  const [selectedSchoolNames, setSelectedSchoolNames] = useState(() =>
    parseMultiFilterValue(searchParams.get('schoolName'))
  );
  const [selectedEntityTypes, setSelectedEntityTypes] = useState([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState(() =>
    parseMultiFilterValue(searchParams.get('groupId'))
  );
  const [selectedMediatorIds, setSelectedMediatorIds] = useState(() =>
    parseMultiFilterValue(searchParams.get('mediatorId'))
  );
  const initialDateFilters = getDateFiltersFromSearchParams(searchParams);
  const [dateFrom, setDateFrom] = useState(initialDateFilters.dateFrom);
  const [dateTo, setDateTo] = useState(initialDateFilters.dateTo);
  const { show } = useLoader();

  const hasActiveFilters = Boolean(
    ['pf', 'diplom', 'ndn'].includes(adminScope) ||
      selectedSchoolIds.length > 0 ||
      (!isCopScope && selectedSchoolNames.length > 0) ||
      selectedEntityTypes.length > 0 ||
      selectedGroupIds.length > 0 ||
      selectedMediatorIds.length > 0 ||
      dateFrom ||
      dateTo
  );

  const scopedSchools = useMemo(
    () => buildScopedSchools(schools, adminScope),
    [schools, adminScope]
  );

  const meetingRows = useMemo(
    () =>
      buildMeetingRows(attendances, scopedSchools, {
        includeEmptyMeetings: isCopScope
      }),
    [attendances, scopedSchools, isCopScope]
  );

  const currentFilters = useMemo(
    () => ({
      selectedSchoolIds,
      selectedSchoolNames,
      selectedEntityTypes,
      selectedGroupIds,
      selectedMediatorIds,
      dateFrom,
      dateTo
    }),
    [
      selectedSchoolIds,
      selectedSchoolNames,
      selectedEntityTypes,
      selectedGroupIds,
      selectedMediatorIds,
      dateFrom,
      dateTo
    ]
  );

  function matchesMeetingFilters(row, excludedKeys = []) {
    const excluded = new Set(excludedKeys);

    const matchesSchool =
      excluded.has('selectedSchoolId') ||
      matchesAnySelected(currentFilters.selectedSchoolIds, row.schoolId);
    const matchesSchoolName =
      isCopScope ||
      excluded.has('selectedSchoolName') ||
      matchesAnySelected(currentFilters.selectedSchoolNames, row.schoolName, normalizeText);
    const matchesGroup =
      excluded.has('selectedGroupId') ||
      matchesAnySelected(currentFilters.selectedGroupIds, row.groupId);
    const matchesEntity = currentFilters.selectedEntityTypes.length === 0 ||
      currentFilters.selectedEntityTypes.some(type => matchesEntityType(row.school || row, type));
    const matchesMediator =
      excluded.has('selectedMediator') ||
      matchesAnySelected(currentFilters.selectedMediatorIds, row.mediatorId);

    const matchesDate =
      excluded.has('dateRange') ||
      isDateInRange(row.meetingDate, {
        dateFrom: currentFilters.dateFrom,
        dateTo: currentFilters.dateTo
      });

    return (
      matchesSchool &&
      matchesSchoolName &&
      matchesEntity &&
      matchesGroup &&
      matchesMediator &&
      matchesDate
    );
  }

  const schoolOptions = useMemo(
    () =>
      buildUniqueOptions(
        meetingRows.filter(row => matchesMeetingFilters(row, ['selectedSchoolId'])),
        row => row.schoolId,
        row => row.schoolCode
      ),
    [meetingRows, currentFilters]
  );

  const groupOptions = useMemo(
    () =>
      buildUniqueOptions(
        meetingRows.filter(row => matchesMeetingFilters(row, ['selectedGroupId'])),
        row => row.groupId,
        row => row.groupName
      ),
    [meetingRows, currentFilters]
  );

  const schoolNameOptions = useMemo(
    () =>
      buildUniqueOptions(
        meetingRows.filter(row =>
          matchesMeetingFilters(row, ['selectedSchoolName'])
        ),
        row => row.schoolName,
        row => row.schoolName
      ),
    [meetingRows, currentFilters]
  );

  const mediatorOptions = useMemo(
    () =>
      buildUniqueOptions(
        meetingRows.filter(row => matchesMeetingFilters(row, ['selectedMediator'])),
        row => row.mediatorId,
        row => row.mediatorName
      ),
    [meetingRows, currentFilters]
  );

  const schoolFilterOptions = useMemo(
    () => [{ id: ALL_FILTER_ID, name: 'Todos los codigos' }, ...schoolOptions],
    [schoolOptions]
  );

  const schoolNameFilterOptions = useMemo(
    () => [
      { id: ALL_FILTER_ID, name: 'Todas las instituciones/organizaciones' },
      ...schoolNameOptions
    ],
    [schoolNameOptions]
  );

  const groupFilterOptions = useMemo(
    () => [{ id: ALL_FILTER_ID, name: 'Todos los grupos' }, ...groupOptions],
    [groupOptions]
  );

  const mediatorFilterOptions = useMemo(
    () => [{ id: ALL_FILTER_ID, name: 'Todos los mediadores' }, ...mediatorOptions],
    [mediatorOptions]
  );

  const selectedSchoolLabel = useMemo(
    () => getMultiFilterLabel(schoolFilterOptions, selectedSchoolIds),
    [schoolFilterOptions, selectedSchoolIds]
  );

  const selectedSchoolNameLabel = useMemo(
    () => getMultiFilterLabel(schoolNameFilterOptions, selectedSchoolNames),
    [schoolNameFilterOptions, selectedSchoolNames]
  );

  const selectedGroupLabel = useMemo(
    () => getMultiFilterLabel(groupFilterOptions, selectedGroupIds),
    [groupFilterOptions, selectedGroupIds]
  );

  const selectedMediatorLabel = useMemo(
    () => getMultiFilterLabel(mediatorFilterOptions, selectedMediatorIds),
    [mediatorFilterOptions, selectedMediatorIds]
  );
  const selectedEntityTypeLabel = selectedEntityTypes
    .map(getEntityTypeFilterLabel).filter(Boolean).join(', ');

  const filtersSearch = useMemo(
    () =>
      buildAttendanceFiltersSearch({
        scope: adminScope,
        schoolId: serializeMultiFilterValue(selectedSchoolIds),
        schoolName: isCopScope ? '' : serializeMultiFilterValue(selectedSchoolNames),
        groupId: serializeMultiFilterValue(selectedGroupIds),
        mediatorId: serializeMultiFilterValue(selectedMediatorIds),
        dateFrom,
        dateTo
      }),
    [
      adminScope,
      selectedSchoolIds,
      selectedSchoolNames,
      selectedGroupIds,
      selectedMediatorIds,
      isCopScope,
      dateFrom,
      dateTo
    ]
  );

  const filteredMeetingRows = useMemo(
    () => meetingRows.filter(row => matchesMeetingFilters(row)),
    [meetingRows, currentFilters]
  );

  function handleSchoolFilter(_, values) {
    setSelectedSchoolIds(normalizeMultiSelection(values));
  }

  function handleSchoolNameFilter(_, values) {
    setSelectedSchoolNames(normalizeMultiSelection(values));
  }

  function handleEntityTypeFilter(_, values) {
    setSelectedEntityTypes(normalizeMultiSelection(values));
  }

  function handleGroupFilter(_, values) {
    setSelectedGroupIds(normalizeMultiSelection(values));
  }

  function handleMediatorFilter(_, values) {
    setSelectedMediatorIds(normalizeMultiSelection(values));
  }

  async function getSchoolsInfo() {
    const response = await getSchools();
    if (!response) {
      return;
    }

    setSchools(response);
  }

  async function fetchAttendancesData() {
    const response = await getAllAttendances();
    if (!response) {
      return null;
    }

    return filterAttendancesByAdminScope(response, adminScope);
  }

  async function loadAttendances({ withLoader = false } = {}) {
    if (withLoader) {
      setDisabledButton(true);
      await show(true);
    }

    try {
      const scopedAttendances = await fetchAttendancesData();
      setAttendances(scopedAttendances);
      setHasLoadedAttendances(true);
      return scopedAttendances;
    } finally {
      if (withLoader) {
        setDisabledButton(false);
        await show(false);
      }
    }
  }

  async function generateReport() {
    return await loadAttendances({ withLoader: true });
  }

  async function downloadControlSocialFormats() {
    if (isDownloadingFormats) return;

    setIsDownloadingFormats(true);
    try {
      const archive = createAttendanceArchive();

      for (const meetingRow of filteredMeetingRows) {
        const meetingId = Number(meetingRow?.id);
        if (!Number.isInteger(meetingId) || meetingId <= 0) continue;

        const meetingInfo = await getMeetingsAttendance(meetingId);
        if (!meetingInfo?.meeting) throw new Error('No fue posible consultar una de las asistencias. Intenta nuevamente.');
        const signedAttendances = Array.isArray(meetingInfo?.attendances)
          ? meetingInfo.attendances.filter(
              attendance =>
                (attendance?.attended === true || attendance?.attended === 1) &&
                Boolean(attendance?.image?.id)
            )
          : [];

        if (!meetingInfo?.meeting || signedAttendances.length === 0) continue;

        const pdfBlob = await buildOfficialAttendancePdfBlob(
          meetingInfo.meeting,
          signedAttendances
        );
        if (!pdfBlob) throw new Error('No fue posible generar uno de los listados. Intenta nuevamente.');

        const activityGroup =
          meetingInfo.meeting?.activityGroup ||
          meetingInfo.meeting?.activity_group ||
          meetingRow?.activityGroup ||
          {};
        const activity = activityGroup?.activity || {};
        const groupCode = String(
          activityGroup?.code || meetingRow?.groupName || `grupo_${meetingId}`
        ).trim();
        const meetingOrder = String(
          meetingInfo.meeting?.order ?? meetingRow?.order ?? '1'
        ).trim();
        const pdfFileName = `FO-GINF-086_Encuentro_${meetingOrder}_${groupCode}.pdf`;

        await archive.add(pdfBlob, {
          comuna: activityGroup?.comuna || 'Sin_comuna',
          activity:
            activity?.name ||
            activity?.line ||
            meetingRow?.schoolName ||
            'Sin_actividad',
          group: groupCode,
          fileName: pdfFileName
        });
      }

      if (archive.count === 0) {
        toast('No hay formatos de asistencia para descargar', {
          type: 'warning'
        });
        return;
      }

      const blob = archive.toBlob();
      const fileName = 'formatos_asistencia_control_social.zip';
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = fileName;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(objectUrl);
      toast('Descarga de formatos iniciada', { type: 'success' });
    } catch (error) {
      console.error(error);
      toast('No se pudieron descargar los formatos de asistencia', {
        type: 'error'
      });
    } finally {
      setIsDownloadingFormats(false);
    }
  }

  function handleClearFilters() {
    setSelectedSchoolIds([]);
    setSelectedSchoolNames([]);
    setSelectedEntityTypes([]);
    setSelectedGroupIds([]);
    setSelectedMediatorIds([]);
    setDateFrom('');
    setDateTo('');
  }

  function handleDateFilterChange(key, value) {
    if (key === 'dateFrom') setDateFrom(value);
    if (key === 'dateTo') setDateTo(value);
  }

  useEffect(() => {
    getSchoolsInfo();
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function restoreAttendances() {
      const scopedAttendances = await fetchAttendancesData();
      if (cancelled) {
        return;
      }

      setAttendances(scopedAttendances);
      setHasLoadedAttendances(true);
    }

    setHasLoadedAttendances(false);
    restoreAttendances();

    return () => {
      cancelled = true;
    };
  }, [adminScope]);

  useEffect(() => {
    const currentSearch = searchParams.toString();
    if (currentSearch === filtersSearch) return;

    const nextSearchParams = new URLSearchParams(filtersSearch);
    setSearchParams(nextSearchParams, { replace: true });
  }, [filtersSearch, searchParams, setSearchParams]);

  return (
    <section className='assistence-main-container'>
      <BreadCrumb />
      <div className='filters-container'>
        <div className='filters-top'>
          <p className='title'>FILTROS DE BUSQUEDA DE ASISTENCIA</p>
        </div>
        <div className='filters-content'>
          <AdminProcessScopeFilter onScopeChange={handleClearFilters} />
          <CustomFilterSelect
            optionsList={schoolFilterOptions}
            placeholder={'Codigo de Proceso Formativo'}
            addInfo={handleSchoolFilter}
            valueEdit={selectedSchoolLabel}
            multiple={true}
            selectedValues={selectedSchoolIds}
          />
          {!isCopScope && (
            <CustomFilterSelect
              optionsList={schoolNameFilterOptions}
              placeholder={'Institucion/organizacion'}
              addInfo={handleSchoolNameFilter}
              valueEdit={selectedSchoolNameLabel}
              multiple={true}
              selectedValues={selectedSchoolNames}
              searchable={true}
              searchPlaceholder={'Buscar institucion u organizacion'}
            />
          )}
          {!isCopScope && (
            <CustomFilterSelect
              optionsList={ENTITY_TYPE_FILTER_OPTIONS}
              placeholder='Tipo de entidad'
              addInfo={handleEntityTypeFilter}
              valueEdit={selectedEntityTypeLabel}
              multiple={true}
              selectedValues={selectedEntityTypes}
            />
          )}
          <CustomFilterSelect
            key={`group-filter-${selectedSchoolIds.join('-') || 'all'}`}
            optionsList={groupFilterOptions}
            placeholder={'Grupo'}
            addInfo={handleGroupFilter}
            valueEdit={selectedGroupLabel}
            multiple={true}
            selectedValues={selectedGroupIds}
            searchable={true}
            searchPlaceholder={'Buscar grupo'}
          />
          <CustomFilterSelect
            optionsList={mediatorFilterOptions}
            placeholder={'Mediador'}
            addInfo={handleMediatorFilter}
            valueEdit={selectedMediatorLabel}
            multiple={true}
            selectedValues={selectedMediatorIds}
            searchable={true}
            searchPlaceholder={'Buscar mediador'}
          />
          <DateRangeFilter
            dateFrom={dateFrom}
            dateTo={dateTo}
            onChange={handleDateFilterChange}
            onClear={() => {
              setDateFrom('');
              setDateTo('');
            }}
          />
        </div>
        <div className='filters-actions-bar'>
          <button
            type='button'
            className='filters-clear-button'
            onClick={handleClearFilters}
            disabled={!hasActiveFilters}
          >
            Limpiar filtros
          </button>
          {isSocialControlScope && (
            <button
              type='button'
              className='attendance-formats-download-button'
              onClick={downloadControlSocialFormats}
              disabled={isDownloadingFormats}>
              {isDownloadingFormats ? 'Preparando...' : 'Descargar'}
            </button>
          )}
          <DownloadAttendances
            attendances={attendances}
            onRequestData={generateReport}
            disabled={disabledButton}
            label={'Generar Reporte'}
            title={'Generar Reporte Asistencias'}
            scope={adminScope}
          />
        </div>
      </div>
      <div className='meetings-main-container'>
        <div className='meeting-top'>
          <p className='title'>ENCUENTROS</p>
          <p className='paginator-text'>
            | Mostrando <span>{filteredMeetingRows.length}</span> de{' '}
            <span>{meetingRows.length}</span> resultados |
          </p>
        </div>

        {filteredMeetingRows.length > 0 ? (
          <div className='info-meetings-content'>
            <ItemsDescription meetingGroups={{ meetingGroups: filteredMeetingRows }} />
          </div>
        ) : (
          <div className='info-meetings-content'>
            <p className='meetings-state'>
              {!hasLoadedAttendances
                ? 'Cargando asistencias...'
                : hasActiveFilters
                  ? 'No hay asistencias que coincidan con los filtros.'
                  : 'Aun no hay asistencias para mostrar.'}
            </p>
          </div>
        )}
      </div>
    </section>
  );
}

export default Assistence;
